/**
 * Locale + country state for the runtime.
 *
 * - `getCurrentLocale()` reads `<html lang="…">` and falls back to
 *   `config.defaultLanguage`.
 * - `getCurrentCountry()` is memoized in `localStorage` (`lmdr_country`) and
 *   falls back to `config.defaultCountry`.
 * - `setCountry(iso)` / `setLanguage(iso)` update the state, dispatch
 *   `smootify:locale_changed`, and force a cart reload so that
 *   `buyerIdentity.countryCode` + Storefront `@inContext` are kept in sync.
 *
 * To avoid an import cycle with `cart/state.ts` (which itself imports
 * `storefront/client.ts` which imports this module), the cart reload is done
 * via dynamic import inside the setters.
 */

import { config } from "../config.js";

const COUNTRY_KEY = "lmdr_country";
const LANGUAGE_KEY = "lmdr_language";

// ---------------------------------------------------------------------------
//  In-memory state (mirrors localStorage + document)
// ---------------------------------------------------------------------------

let country: string | null = null;
let language: string | null = null;
let autoDetected = false;

// ---------------------------------------------------------------------------
//  Safe storage
// ---------------------------------------------------------------------------

function safeGet(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
//  Normalization
// ---------------------------------------------------------------------------

function normalize(iso: string): string {
  return iso.trim().toUpperCase().slice(0, 2);
}

// ---------------------------------------------------------------------------
//  Getters
// ---------------------------------------------------------------------------

export function getCurrentLocale(): string {
  if (language) return language;
  // Priorité 1 : choix user persisté
  const stored = safeGet(LANGUAGE_KEY);
  if (stored && stored.length >= 2) {
    language = normalize(stored);
    return language;
  }
  // Priorité 2 : auto-detect navigator (1er visit uniquement) — important :
  // priorisé sur <html lang> qui est statique Webflow et ne reflète pas le
  // browser de l'user (un Américain verrait FR sinon).
  if (
    config.selectMarketBasedOnBrowserLanguage &&
    typeof navigator !== "undefined" &&
    navigator.language
  ) {
    autoDetected = true;
    language = normalize(navigator.language);
    safeSet(LANGUAGE_KEY, language);
    return language;
  }
  // Priorité 3 : <html lang>
  if (typeof document !== "undefined") {
    const fromHtml = document.documentElement.lang;
    if (fromHtml && fromHtml.length >= 2) {
      language = normalize(fromHtml);
      return language;
    }
  }
  // Fallback : config default
  language = normalize(config.defaultLanguage);
  return language;
}

export function getCurrentCountry(): string {
  if (country) return country;
  // Priorité 1 : choix user persisté
  const stored = safeGet(COUNTRY_KEY);
  if (stored && stored.length >= 2) {
    country = normalize(stored);
    return country;
  }
  // Priorité 2 : auto-detect navigator (1er visit, e.g. "fr-FR" → FR)
  if (
    !autoDetected &&
    config.selectMarketBasedOnBrowserLanguage &&
    typeof navigator !== "undefined" &&
    navigator.language
  ) {
    autoDetected = true;
    const parts = navigator.language.split("-");
    const ccFromBrowser = parts[1] ?? parts[0];
    if (ccFromBrowser && ccFromBrowser.length >= 2) {
      country = normalize(ccFromBrowser);
      // Persiste pour ne pas re-detect à chaque page (et garder cohérence
      // entre tabs si multi-langues du browser)
      safeSet(COUNTRY_KEY, country);
      return country;
    }
  }
  // Fallback : config default
  country = normalize(config.defaultCountry);
  return country;
}

// ---------------------------------------------------------------------------
//  Setters
// ---------------------------------------------------------------------------

function dispatchChange(): void {
  if (typeof document === "undefined") return;
  document.dispatchEvent(
    new CustomEvent("smootify:locale_changed", {
      detail: { country: getCurrentCountry(), language: getCurrentLocale() },
    }),
  );
}

async function refreshCartBuyerIdentity(): Promise<void> {
  // Dynamic import to avoid a module-init cycle.
  try {
    const mod = await import("../cart/state.js");
    await mod.reloadCart({ syncBuyerIdentity: true });
  } catch {
    /* cart not yet loaded — no-op */
  }
}

export async function setCountry(iso: string): Promise<void> {
  const next = normalize(iso);
  if (!next || next.length !== 2) return;
  if (country === next) return;
  country = next;
  safeSet(COUNTRY_KEY, next);
  dispatchChange();
  await refreshCartBuyerIdentity();
}

export async function setLanguage(iso: string): Promise<void> {
  const next = normalize(iso);
  if (!next || next.length !== 2) return;
  if (language === next) return;
  language = next;
  safeSet(LANGUAGE_KEY, next);
  if (typeof document !== "undefined") {
    try {
      document.documentElement.lang = next.toLowerCase();
    } catch {
      /* read-only in some contexts */
    }
  }
  dispatchChange();
  await refreshCartBuyerIdentity();
}

/**
 * À appeler au boot : force la résolution country/language (auto-detect si pas
 * de localStorage), puis sync le cart `buyerIdentity.countryCode` si l'user
 * n'avait pas encore de cart dans ce country.
 *
 * Ne fait PAS de reload (différence avec setLocale) — on est au tout début du
 * boot, l'app n'a encore rien rendered. Toutes les queries Storefront qui
 * suivront utiliseront automatiquement le bon country/language via @inContext.
 */
export async function bootstrapLocale(): Promise<void> {
  // Force resolution (déclenche auto-detect si applicable)
  const cc = getCurrentCountry();
  const lc = getCurrentLocale();
  // Sync <html lang> pour SEO + a11y
  if (typeof document !== "undefined") {
    try {
      document.documentElement.lang = lc.toLowerCase();
    } catch {
      /* noop */
    }
  }
  // Sync cart buyerIdentity (sans reload — boot context)
  void refreshCartBuyerIdentity();
  // Dispatch pour signaler aux autres modules que le locale est prêt
  if (typeof document !== "undefined") {
    document.dispatchEvent(
      new CustomEvent("smootify:locale_ready", {
        detail: { country: cc, language: lc },
      }),
    );
  }
}

/**
 * Switch combiné country + language en une seule opération.
 *
 * Flow :
 *   1. Pose les nouveaux country/language en mémoire + localStorage
 *   2. Invalide le cache Storefront (toutes les queries devront re-fetch
 *      dans le nouveau market context : prix devises, traductions)
 *   3. Update cart `buyerIdentity.countryCode` (Shopify retourne le cart
 *      avec la nouvelle currency)
 *   4. Optionnel : reload la page pour forcer re-render de tous les produits
 *      hydratés (Smootify natif fait ça aussi)
 *
 * @param opts.reload - default true. Si false, l'app doit re-render manuellement.
 */
export async function setLocale(
  iso: { country: string; language?: string },
  opts: { reload?: boolean } = {},
): Promise<void> {
  const reload = opts.reload !== false;
  const nextCountry = normalize(iso.country);
  const nextLanguage = iso.language ? normalize(iso.language) : language ?? normalize(config.defaultLanguage);
  if (!nextCountry || nextCountry.length !== 2) return;

  let changed = false;
  if (country !== nextCountry) {
    country = nextCountry;
    safeSet(COUNTRY_KEY, nextCountry);
    changed = true;
  }
  if (nextLanguage && language !== nextLanguage) {
    language = nextLanguage;
    safeSet(LANGUAGE_KEY, nextLanguage);
    if (typeof document !== "undefined") {
      try {
        document.documentElement.lang = nextLanguage.toLowerCase();
      } catch {
        /* noop */
      }
    }
    changed = true;
  }
  if (!changed) return;

  // Invalide le cache Storefront pour forcer re-fetch dans le nouveau context.
  try {
    const storefrontMod = await import("../storefront/client.js");
    storefrontMod.storefront.invalidateCache();
  } catch {
    /* noop */
  }

  // Invalide le cache markets aussi.
  try {
    const marketsMod = await import("./markets.js");
    marketsMod.invalidateMarketsCache();
  } catch {
    /* noop */
  }

  dispatchChange();

  // Update cart buyerIdentity → Shopify retourne cart en nouvelle currency.
  await refreshCartBuyerIdentity();

  // Reload page pour re-render tous les produits hydratés (smootify-product
  // ne se re-render pas tout seul au locale change).
  if (reload && typeof window !== "undefined") {
    window.location.reload();
  }
}
